/**
 * ADR-0243 (security review M3) — Settings → General → Returning visitors.
 *
 * The per-site switch for per-human visitor tracking. Until it is on, the
 * embed keeps no visitor id in the browser and Sill stores no visitor IP
 * for this site. Default comes from `HUMAN_VISITOR_CAPTURE_DEFAULT`
 * (server-reported, so the dashboard never hard-codes it).
 *
 * Owner / admin may change it; reviewer / viewer see nothing (same
 * "hide what the role can't use" rule as the buyer-detail retention card).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { UserRole } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { getVisitorCapture, putVisitorCapture, saveFailureReason, type VisitorCaptureState } from '../../api/people.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useToast } from '../../app/providers.js';
import { Card, Toggle } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface ReturningVisitorsSectionProps {
  readonly siteId: string;
  readonly role_override?: UserRole | undefined;
}

export function ReturningVisitorsSection({ siteId, role_override }: ReturningVisitorsSectionProps): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const toast = useToast();
  const role: UserRole | null = useMemo(() => {
    if (role_override !== undefined) return role_override;
    return auth !== null && auth.state.kind === 'authenticated' ? auth.state.user.role : null;
  }, [role_override, auth]);
  const can_write = role === 'owner' || role === 'admin';
  const [state, setState] = useState<VisitorCaptureState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!can_write || http === null) return undefined;
    let cancelled = false;
    void getVisitorCapture(http, siteId)
      .then((s) => { if (!cancelled) setState(s); })
      .catch(() => { if (!cancelled) setState(null); });
    return () => { cancelled = true; };
  }, [can_write, http, siteId]);

  const onToggle = useCallback((next: boolean) => {
    if (http === null || state === null) return;
    const previous = state;
    setSaving(true);
    setState({ ...state, enabled: next, setting: next });
    void putVisitorCapture(http, siteId, next)
      .then((s) => setState(s))
      .catch((err: unknown) => {
        setState(previous);
        toast({ tone: 'coral', title: 'Could not save the returning-visitor setting.', body: saveFailureReason(err) });
      })
      .finally(() => setSaving(false));
  }, [http, siteId, state, toast]);

  if (!can_write) return null;

  return (
    <Card className="p-5 sm:p-6 mb-5">
      <div data-testid="returning-visitors-section" className="flex items-start gap-4">
        <div className="flex-1">
          <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>
            RETURNING VISITORS
          </div>
          <div style={{ fontFamily: serifFont, fontSize: 22, color: C.text, letterSpacing: -0.5, marginBottom: 4 }}>
            Recognise people who come back
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            When on, the embed keeps a random visitor ID in your visitors&apos; browsers and Sill records the IP
            address of each human visit, so you can see the same person across days and months. Browsers that
            send Global Privacy Control are never tracked. Visitor history is kept for 13 months.
            {state !== null && state.setting === null ? ' Currently following the Sill default.' : ''}
          </div>
        </div>
        {state !== null ? (
          <div style={{ opacity: saving ? 0.6 : 1 }}>
            <Toggle on={state.enabled} onChange={onToggle} disabled={saving} aria-label="Recognise returning visitors" />
          </div>
        ) : null}
      </div>
    </Card>
  );
}
